<template>
    <div>
        <v-row no-gutters>
            <v-col>
                <ModernFilterBar
                    :filter-controller="filterController"
                >
                    <template v-slot:right="{filters, filterAdd}">
                        <AddFilterButton
                            :filters="filters"
                            @add_filter="filterAdd($event)"
                        />

                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <icon-button
                                    :on="on"
                                    icon="Delete"
                                    @click="filterController.resetFilters()"
                                />
                            </template>
                            <span>{{ $t("Clear filters") }}</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <icon-button
                                    :disabled="Object.keys($route.query).length < 1"
                                    :on="on"
                                    icon="Save"
                                    @click="onSaveFiltersClick"
                                />
                            </template>
                            <span>{{ $t("Save") }}</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <icon-button
                                    :loading="richDataTableController.isLoading()"
                                    :on="on"
                                    icon="RotateCw"
                                    @click="urlAdapter.updateUrlParameters()"
                                />
                            </template>
                            <span>{{ $t("Update") }}</span>
                        </v-tooltip>
                    </template>
                </ModernFilterBar>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="9">
                <v-card
                    :height="'100%'"
                    :class="!detailedStatisticsOpen ? 'elevation-0 transparent' : ''"
                    style="transition: none"
                >
                    <v-row no-gutters class="align-center pt-2">
                        <div class="caption grey--text font-weight-medium px-3"
                             style="flex-wrap: nowrap; white-space: nowrap">
                            {{ friendly_selected_dates }}
                        </div>
                        <v-spacer></v-spacer>
                        <action-menu
                            :items="actionItems"
                            @action="actionItems[$event].action()"
                        >
                            <template v-slot:activator="{ on }">
                                <v-btn
                                    v-on="on"
                                    text
                                    x-small
                                    color="n3"
                                    class="caption font-weight-medium"
                                >
                                    <icon
                                        icon="Download"
                                        size="20"
                                    />
                                    <div class="ml-2">
                                        {{ $t('Download') }}
                                    </div>
                                </v-btn>
                            </template>
                        </action-menu>
                        <v-btn
                            text
                            x-small
                            color="n3"
                            class="caption font-weight-medium mr-1"
                            @click="detailedStatisticsClick"
                        >
                            <icon
                                :icon="detailedStatisticsOpen ? 'EyeOff' : 'Eye'"
                                size="20"
                            />
                            <div class="ml-2">
                                {{ detailedStatisticsOpen ? $t("Hide") : $t("Show") }}
                            </div>
                        </v-btn>
                    </v-row>
                    <v-row no-gutters>
                        <div class="headline px-3 pb-1"
                             style="flex-wrap: nowrap; white-space: nowrap">
                            {{
                                f_bookmarks("report").find(i => activeBookmarkRule(i))
                                    ? f_bookmarks("report").find(i => activeBookmarkRule(i)).name
                                    : $t("Report")
                            }}
                        </div>
                    </v-row>

                    <ReportPeriodChart
                        v-if="detailedStatisticsOpen && graphData"
                        ref="periodChart"
                        :graphSegments="graphData"
                    />

                </v-card>
            </v-col>
            <v-col cols="3">
                <ReportSummaryStatistics
                    :total-columns="totalColumns"
                    :billing-info="billingInfo"
                    :total-tasks="totalTasks"
                    :detailed-statistics-open="detailedStatisticsOpen"
                />
            </v-col>
        </v-row>

        <v-row no-gutters justify="center">
            <v-col>
                <v-row
                    no-gutters
                    align="center"
                >
                    <v-col>
                        <v-row no-gutters>
                            <v-tabs
                                :value="selectedTabIndex"
                                background-color="transparent"
                                class="denser-tabs"
                                slider-size="4"
                            >
                                <v-tab
                                    v-for="tab in tabs"
                                    :key="tab.value"
                                    @click="onTabClick(tab.value)"
                                >
                                    <span class="caption font-weight-medium">
                                        {{ tab.text }}
                                    </span>
                                </v-tab>
                            </v-tabs>
                        </v-row>
                    </v-col>
                    <v-col>
                        <v-row
                            justify="end"
                            no-gutters
                            align="center"
                        >
                            <action-menu
                                :items="exportOptions"
                                icon="Download"
                                @action="exportOptions[$event].action()"
                            />
                            <ColumnController
                                :headers="richDataTableController.getAllHeaders()"
                                :visible-headers="richDataTableController.getVisibleHeaders().map(x => x.value)"
                                dense
                                @update_columns="richDataTableController.setVisibleHeadersWithoutLoadData($event)"
                            />
                        </v-row>
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <component
                        v-if="selectedTabIndex !== -1"
                        :is="tabs[selectedTabIndex].component"
                        :show-time-feedback="showTimeFeedback"
                        :show-time-feedback-options="showTimeFeedbackOptions"
                        :show-new-day-feedback="showNewDayFeedback"
                        :current-view="tabs[selectedTabIndex].value"
                        :grouping="urlAdapter.getUrlParameter('groupBy') || 'client;topic'"
                        :group-toggle-reset="group_toggle_reset_unique"
                        :sort-by="urlAdapter.getUrlParameter('sortBy')"
                        :sort-desc="urlAdapter.getUrlParameter('sortDesc')"
                        :view="urlAdapter.getUrlParameter('view')"
                        :urlAdapter="urlAdapter"
                        :rich-data-table-controller="richDataTableController"
                        :summary_childs="summary_childs"
                        @sortBy="richDataTableController.changeSort($event)"
                        @export_details="show_export_details = $event ? 1 : 0"
                        @update:grouping="urlAdapter.setUrlParameter('groupBy', $event)"
                        @updateTasks="showSnackbar"
                    />
                </v-row>
            </v-col>
        </v-row>
        <v-snackbar
            :value="data_changed_snackbar"
            :timeout="0"
            bottom
            multi-line
        >
            <span>{{ $t("Report data has changed. Please update report.") }}</span>
            <v-btn
                text
                @click="snackbarClose"
            >
                {{ $t("Close") }}
            </v-btn>
            <primary-button
                color="primary"
                @click="snackbarUpdate"
            >
                <icon
                    left
                    icon="RotateCw"
                />
                <span>{{ $t("Update") }}</span>
            </primary-button>
        </v-snackbar>
    </div>
</template>

<script>
import {ref, computed, reactive} from "@vue/composition-api";
import NewFilterBar from "../../components/NewFilterBar";
import AddFilterButton from "../../components/NewFilterBar/components/AddFilterButton";
import {$t} from "@/i18n";
import ReportModule, {state} from "../../store/modules/report";
import DialogModule from "../../store/modules/dialog";
import ReportSummary from "./ReportSummary";
import ReportTaskTime from "./ReportTaskTime.vue";
import BookmarkMenu from "../../composables/use-bookmark-menu";
import DropMenu from "../../components/NewFilterBar/components/DropMenu";

import {
    formatDate,
    formatSysDate,
    parseSysDate,
    formatTimeDuration,
} from "@/plugins/formatting";
import Vue from "vue";
import {debounce} from "throttle-debounce";
import AccountModule from "../../store/modules/account";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import ColumnController from "../../components/ColumnController";
import ModernFilterBar from "@/components/ModernFilterBar";
import UrlAdapter from "@/urlAdapter/urlAdapter";
import FilterBarController from "@/components/ModernFilterBar/FilterBarController";
import RichDataTableController from "@/components/RichDataTable/RichDataTableController";
import ApiReport from "@/api/report/report"
import {RICH_DATA_TABLE_COMPONENT} from "@/components/RichDataTable/richDataTableComponents";
import TaskModule from "@/store/modules/task";
import TaskWsModule from "@/store/modules/task_ws";
import TimerModule from "@/store/modules/timer";
import {
    FEATURE_WORK_ON_EXISTING_TASK,
    validateFeatureAllowed
} from "@/modules/subscription/allowed_features";
import {translatePeriods} from "@/modules/time/time";
import ReportFlextime from "@/views/Report/ReportFlextime";
import BookmarkModule from "@/store/modules/bookmark";
import ProjectModule from "@/store/modules/project";
import VueApexCharts from 'vue-apexcharts';
import ReportPeriodChart from "./Graphs/ReportPeriodChart.vue";
import ReportProgressBar from "../../components/Graphs/ProgressBar.vue";
import ReportSummaryStatistics from "./ReportSummaryStatistics.vue";
import ReportDefaultColumns from "./ReportDefaultColumns";
import {useTopicStore} from "../../store/modules/topic";
import {useMemberStore} from "../../store/modules/member";

export default {
    name: "Report",
    components: {
        ReportSummaryStatistics,
        ReportProgressBar,
        ReportPeriodChart,
        NewFilterBar,
        ReportSummary,
        DropMenu,
        ReportTaskTime,
        AddFilterButton,
        ColumnController,
        ModernFilterBar,
        ReportFlextime,
        apexchart: VueApexCharts,
    },
    setup(props, ctx) {
        const {current_account, getAccount, isFeatureEnabledAnywhere, isOfficeAttendanceTrackingEnabledAnyWhere} = AccountModule();
        const taskWsModule = TaskWsModule();
        const timerModule = TimerModule();
        const bookmarkModule = BookmarkModule()
        const memberStore = useMemberStore()
        const topicStore = useTopicStore()

        const periodChart = ref(null)

        const {
            default_summary_columns,
            default_task_columns,
            default_entries_columns,
            default_flextime_columns
        } = ReportDefaultColumns();

        const summary_childs = ref([]);
        const billableTime = ref(null);
        const nonBillableTime = ref(null);
        const estimationTime = ref(null)
        const totalTime = ref(null);
        const extraTime = ref(null);
        const balanceTime = ref(null);
        const agreedTime = ref(null);
        const tableWidth = ref(null);
        let isReportStatisticsOpen = JSON.parse(localStorage.getItem('isReportStatisticsOpen'))
        if (isReportStatisticsOpen === null) {
            isReportStatisticsOpen = true
        }

        const detailedStatisticsOpen = ref(isReportStatisticsOpen)
        const {isFlextimeActive} = ReportModule()
        const statistics = ref(null)

        const {navigate_bookmark, f_bookmarks, activeBookmarkRule, onDeleteBookmarkClick} = BookmarkMenu();
        const {openDialog, setDialogOptions} = DialogModule();
        const dialogModule = DialogModule()
        const {isEventTypeCompany} = ProjectModule();
        const {
            clients,
            companies,
            client_groups,
            details_custom_fields,
            summary_custom_fields,
            time_custom_fields,
            flextime_custom_fields,
            report,
            projects
        } = ReportModule();
        const {openTask, setLastTouchedTask} = TaskModule();

        const urlAdapter = new UrlAdapter()
        urlAdapter.loadDefaultBookmark('report', bookmarkModule.bookmarks.value)
        /** @type {FilterBarController} */
        const filterController = reactive(FilterBarController.init(urlAdapter))

        const currentView = computed(() => {
            if (urlAdapter.getUrlParameter('view')) {
                return urlAdapter.getUrlParameter('view')
            }
            return 'summary'
        })

        const groupBy = computed(() => {
            if (urlAdapter.getUrlParameter('groupBy')) {
                return urlAdapter.getUrlParameter('groupBy')
            }
            return 'client;topic'
        })

        const usersWithoutGroups = computed(() => memberStore.getAllPeople())
        const users = computed(() => memberStore.getAllMembers())

        const defaultDelegatedToItems = computed(() => memberStore.getAllPeople().map(x => x.id).flat(1))

        const visibleUsersOrSelectedInactiveUsers = computed(() => {
            return memberStore.getAllMembers(true).filter(user => {
                if (['active', 'pending', 'limited'].includes(user.status)) {
                    return true
                }

                const selectedPersonIds = urlAdapter.getUrlParameter('person_id_list')
                if (selectedPersonIds && Array.isArray(user.id) && selectedPersonIds.find(x => user.id.includes(x))) {
                    return true
                } else if (selectedPersonIds && selectedPersonIds.includes(user.id)) {
                    return true
                }

                return false
            })
        })

        const visibleClientsOrSelectedInactiveClients = computed(() => {
            return clients.value.filter(client => {
                if (client.status === 'active') {
                    return true
                }

                const selectedClientIds = urlAdapter.getUrlParameter('client_id_list')
                if (selectedClientIds && selectedClientIds.includes(client.id)) {
                    return true
                }

                return false
            })
        })

        const tabs = computed(() => {
            let tabs = [
                {text: $t("Summary"), component: "ReportSummary", value: "summary"},
                {text: $t("Tasks"), component: "ReportTaskTime", value: "detailed"},
                {text: $t("Time"), component: "ReportTaskTime", value: "time_entries"}
            ]

            if (isFlextimeActive.value) {
                tabs.push({text: $t("Flextime"), component: "ReportFlextime", value: "flextime"})
            }

            return tabs
        });

        const getStartOfSelectedDuration = (value, durationType) => {
            if (durationType === 'weekly') {
                if (value && value.includes('sow_')) {
                    return value
                } else {
                    return 'sow_0'
                }
            } else if (durationType === 'monthly') {
                if (value && value.includes('som_')) {
                    return value
                } else {
                    return 'som_0'
                }
            }

            return value
        }
        const getEndOfSelectedDuration = (value, durationType) => {
            if (durationType === 'weekly') {
                if (value && value.includes('eow_')) {
                    return value
                } else {
                    return 'eow_0'
                }
            } else if (durationType === 'monthly') {
                if (value && value.includes('eom_')) {
                    return value
                } else {
                    return 'eom_0'
                }
            }

            return value
        }

        const selectedTabIndex = computed(() => {
            if (tabs.value.findIndex(tab => tab.value === urlAdapter.getUrlParameter('view')) >= 0) {
                return tabs.value.findIndex(tab => tab.value === urlAdapter.getUrlParameter('view'))
            }

            return 0;
        });

        const companiesWithFlextime = computed(() => companies.value.filter(x => x.flextime_settings))
        const getSelectedCompanyFlextimeSettings = (companyId) => {
            if (!companyId) {
                return null
            }

            let company = companiesWithFlextime.value.find(x => x.id === companyId)
            if (!company) {
                return null
            }

            return company.flextime_settings
        }

        const summaryTabIndex = 0;
        const taskTabIndex = 1;
        const timeTabIndex = 2;
        const flextimeTabIndex = 3;

        const topics = computed(() => topicStore.getAllTopics(true))

        const getFilters = (selectedTabIndex, accountId) => {
            let _filters = [
                {
                    key: "dates_list",
                    default_value: [
                        new Date().toISOString().split("T")[0],
                        new Date().toISOString().split("T")[0],
                        "sow_0",
                        "eow_0"
                    ],
                    durationType: getSelectedCompanyFlextimeSettings(accountId) ? getSelectedCompanyFlextimeSettings(accountId).type : null,
                    default: true,
                    forceFirstDayOfWeek: selectedTabIndex === flextimeTabIndex ? 1 : null
                },
                {
                    key: "person_id_list",
                    default_value: [current_account.value.id],
                    items: visibleUsersOrSelectedInactiveUsers,
                    default: true
                },
                {
                    key: "client_id_list",
                    default_value: [],
                    items: visibleClientsOrSelectedInactiveClients.value,
                    default: true
                },
                {key: "topic_id_list", default_value: [], items: topics, default: true},
                {key: "task_status", default_value: [], default: true},
                {key: "keyword", default_value: [""], default: true},
                {key: "client_group_id_list", default_value: [], items: client_groups},

                selectedTabIndex === flextimeTabIndex ? {
                    key: "account_id",
                    default_value: [companiesWithFlextime.value.length ? companiesWithFlextime.value[0].id : null],
                    default: true,
                    removable: false,
                    items: companiesWithFlextime
                } : {key: "account_id_list", default_value: [], items: companies},

                {key: "is_important", default_value: ["1"]},
                {key: "is_extra", default_value: ["1"]},
                {key: "is_recurring", default_value: ["1"]},
                {key: "is_commented", default_value: ["1"]},
                {key: "is_tracked_time", default_value: ["1"]},
                {key: "is_estimated", default_value: ["1"]},
                {key: "delegated_by", default_value: [current_account.value.id], items: usersWithoutGroups},
                {key: "delegated_to", default_value: defaultDelegatedToItems.value, items: users},
                {key: "is_cp", default_value: ["1"], feature: 'client_portal'},
                {key: "project_id_list", default_value: [], feature: 'project', items: projects.value}
            ]

            if ([summaryTabIndex, taskTabIndex, timeTabIndex].includes(selectedTabIndex)) {
                _filters.push({key: "bill_by", default_value: [], default: false})
            }

            const result = _filters.filter(x => x.feature === undefined || isFeatureEnabledAnywhere(x.feature))
            // console.info('report_flextime report _filters', result)
            return result
        }

        const filters = computed(() => getFilters(selectedTabIndex.value, urlAdapter.getUrlParameter('account_id') ? urlAdapter.getUrlParameter('account_id')[0] : null))

        const beforeFilterInput = function (filter_key, value) {
            if (['account_id'].includes(filter_key)) {
                let accountId = value[0].id
                filterController.initFilters(getFilters(selectedTabIndex.value, accountId))

                if (accountId) {
                    let dateList = urlAdapter.getUrlParameter('dates_list')
                    let durationType = getSelectedCompanyFlextimeSettings(accountId) ? getSelectedCompanyFlextimeSettings(accountId).type : null
                    if (durationType) {
                        const startPeriod = getStartOfSelectedDuration(dateList[2], durationType)
                        const endPeriod = getEndOfSelectedDuration(dateList[3], durationType)

                        const forceFirstDayOfWeek = urlAdapter.getUrlParameter('view') === 'flextime' ? 1 : null
                        const startDate = translatePeriods(startPeriod, forceFirstDayOfWeek, 'beforeFilterInput 1')
                        const endDate = translatePeriods(endPeriod, forceFirstDayOfWeek, 'beforeFilterInput 2')

                        urlAdapter.setUrlParametersFromObject({
                            dates_list: [startDate, endDate, startPeriod, endPeriod],
                        }, false, true)
                    }
                }
            }
        }

        filterController.setBeforeFilterInputFunc((...a) => beforeFilterInput(...a))
        filterController.initFilters(filters.value)

        const onRowClick = (item) => {
            const companyAccount = getAccount(item.account_id)

            if (companyAccount.value) {
                validateFeatureAllowed(FEATURE_WORK_ON_EXISTING_TASK, companyAccount.value)
            }
            const openById = item.task_id ? item.task_id : item.id ? item.id : item.virtual_id
            openTask(openById, {admin_view: true});

            const taskData = {
                taskId: openById,
                taskTitle: item.title,
                clientId: item.client_id,
                clientColor: item.client.color,
                clientInitials: item.client.client_initials
            }
            setLastTouchedTask(taskData)
        }

        const options = [
            {key: "sortBy", default_value: ""},
            {key: "sortDesc", default_value: false},
            {key: "itemsPerPage", default_value: 50},
            {key: "page", default_value: 1}
        ]

        const data_changed_snackbar = ref(false);

        const group_toggle_reset_unique = ref(0);
        let show_export_details = ref(0);

        urlAdapter.addGetStateCallback((...args) => ({
            view: currentView.value,
            groupBy: groupBy.value,
            active_bookmark_id: urlAdapter.getUrlParameter('active_bookmark_id')
        }))

        const has_event_type_company = computed(() => {
            return !!companies.value.find(
                (account) => isEventTypeCompany(account.id)
            );
        })

        const fieldColumns = computed(() => {
            let ret = []

            let column_format = cf => {
                return {
                    text: cf.title,
                    value: cf.name,
                    class: "text-no-wrap",
                    type: cf.type,
                    group: cf.group,
                    component: cf.component,
                    align: cf.align,
                    justify: cf.justify
                };
            };
            if (selectedTabIndex.value === summaryTabIndex) {
                ret = ret.concat([
                    {
                        text: "",
                        value: "avatar",
                        align: "left pl-1",
                        sortable: false,
                        width: 31,
                        removable: false,
                        alwaysVisible: true
                    },
                    {
                        text: "",
                        value: "progress",
                        align: "left pl-1",
                        sortable: false,
                        removable: false,
                        alwaysVisible: true
                    }
                ])

                const c_fields = summary_custom_fields.value.map(cf => column_format(cf));
                ret = ret.concat(c_fields)

                ret = ret.concat([
                    {
                        text: $t("Extra work"),
                        value: "extra",
                        align: "right pl-1",
                        justify: "end",
                        /*width: 94,*/
                        sortable: true,
                        removable: false,
                        alwaysVisible: true
                    },
                    {
                        text: $t("Total"),
                        value: "total",
                        align: "right pl-1",
                        justify: "end",
                        sortable: true,
                        removable: false,
                        alwaysVisible: true
                    }
                ])

            } else if (selectedTabIndex.value === taskTabIndex) {
                ret = ret.concat([
                    {
                        name: 'client',
                        text: $t('Client'),
                        value: 'client',
                        class: 'pl-11',
                        alwaysVisible: true,
                        sortable: true,
                    },
                    {
                        name: 'title',
                        text: $t('Task'),
                        value: 'title',
                        alwaysVisible: true
                    },
                    {
                        name: 'client_default_person',
                        text: $t('Client default person'),
                        value: 'client_default_person',
                        alwaysVisible: false,
                        sortable: true
                    }
                ])

                const c_fields = details_custom_fields.value.map(cf => column_format(cf));
                ret = ret.concat(c_fields)
            } else if (selectedTabIndex.value === timeTabIndex) {
                ret = ret.concat([
                    {
                        name: 'client',
                        text: $t('Client'),
                        class: 'pl-11',
                        value: 'client',
                        alwaysVisible: true,
                        sortable: true,
                    },
                    {
                        name: 'title',
                        text: $t('Task'),
                        value: 'title',
                        alwaysVisible: true
                    }
                ])

                const c_fields = time_custom_fields.value.map(cf => column_format(cf));
                ret = ret.concat(c_fields)
                ret.push({
                    name: 'timeFeedback',
                    value: 'timeFeedback',
                    alwaysVisible: true,
                    sortable: false
                })
            } else if (selectedTabIndex.value === flextimeTabIndex) {
                ret = ret.concat([
                    {
                        text: "",
                        value: "avatar",
                        align: "left pl-1",
                        sortable: false,
                        width: 31,
                        removable: false,
                        alwaysVisible: true
                    },
                ])

                const c_fields = flextime_custom_fields.value.map(cf => column_format(cf));
                ret = ret.concat(c_fields)

                if (!isOfficeAttendanceTrackingEnabledAnyWhere()) {
                    ret = ret.filter(x => x.value !== 'flextime_office_time')
                    ret = ret.filter(x => x.value !== 'flextime_office_agreed_time')
                    ret = ret.filter(x => x.value !== 'flextime_office_percent')
                }
            }

            if (!isOfficeAttendanceTrackingEnabledAnyWhere()) {
                ret = ret.filter(x => x.value !== 'office')
            }

            if ([taskTabIndex, timeTabIndex].includes(selectedTabIndex.value) && isFeatureEnabledAnywhere('project')) {
                ret = ret.concat([
                    {
                        text: has_event_type_company.value ? $t('Event') : $t("Project"),
                        value: "project_id",
                        align: "left",
                        sortable: false,
                        removable: true,
                        alwaysVisible: false
                    },
                ])
            }

            ret = ret.map(x => {
                if (selectedTabIndex.value === summaryTabIndex && default_summary_columns.includes(x.value)) {
                    x.default = true
                } else if (selectedTabIndex.value === taskTabIndex && default_task_columns.includes(x.value)) {
                    x.default = true
                } else if (selectedTabIndex.value === timeTabIndex && default_entries_columns.includes(x.value)) {
                    x.default = true
                }

                return x
            })

            return ret
        });

        /** @type {RichDataTableController} */
        const richDataTableController = reactive(RichDataTableController.init(fieldColumns.value, options, urlAdapter))
        richDataTableController.setOnRowClick((...a) => onRowClick(...a))

        const totalColumns = computed(() => {
            let ret = [
                {
                    label: $t("Total"),
                    value: formatTimeDuration(totalTime.value),
                    valueClasses: ['headline'],
                    labelClasses: ['body-2'],
                }
            ];

            if (selectedTabIndex.value === flextimeTabIndex) {
                ret = ret.concat([
                    {
                        label: $t("Agreed"),
                        value: formatTimeDuration(agreedTime.value),
                    },
                    {
                        label: $t("Period balance"),
                        value: formatTimeDuration(balanceTime.value),
                    }
                ])
            } else {
                let non_billable = richDataTableController.getVisibleHeaders().find(x => x.value === 'non_billable')
                let billable = richDataTableController.getVisibleHeaders().find(x => x.value === 'billable')
                let estimation = richDataTableController.getVisibleHeaders().find(x => x.value === 'estimation')

                if (non_billable) {
                    ret.push({
                        label: $t('Non billable'),
                        value: formatTimeDuration(nonBillableTime.value),
                        valueClasses: ['body-2', 'grey--text'],
                    });
                }

                ret.push({
                        label: $t("Extra work"),
                        value: formatTimeDuration(extraTime.value),
                        valueClasses: ['body-2', 'grey--text'],
                    }
                );

                if (billable) {
                    ret.push({
                        label: $t('Billable'),
                        value: formatTimeDuration(billableTime.value),
                    });
                }

                if (estimation) {
                    ret.push({
                        label: $t('Estimation'),
                        value: formatTimeDuration(estimationTime.value),
                        valueClasses: ['body-2', 'grey--text'],
                        tooltip: $t('Estimation report shows based on task start date')
                    });
                }
            }

            return ret;
        });


        let input_data = ref([]);

        const onTabClick = async (val) => {
            let currentTab = urlAdapter.getUrlParameter('view')

            let visibleHeaders = []
            if (val === 'summary') {
                visibleHeaders = default_summary_columns
            } else if (val === 'detailed') {
                visibleHeaders = default_task_columns
            } else if (val === 'time_entries') {
                visibleHeaders = default_entries_columns
            } else if (val === 'flextime') {
                visibleHeaders = default_flextime_columns
            }

            let params = {
                view: val,
                columns: visibleHeaders
            }

            if (val === 'flextime') {
                params['account_id_list'] = null
                params['account_id'] = companiesWithFlextime.value.length ? companiesWithFlextime.value[0].id : null
                params['groupBy'] = 'assignee;date'
                let dateList = urlAdapter.getUrlParameter('dates_list')
                let durationType = getSelectedCompanyFlextimeSettings(params['account_id']) ? getSelectedCompanyFlextimeSettings(params['account_id']).type : null

                if (durationType) {
                    const startPeriod = getStartOfSelectedDuration(dateList[2], durationType)
                    const endPeriod = getEndOfSelectedDuration(dateList[3], durationType)

                    const forceFirstDayOfWeek = val === 'flextime' ? 1 : null
                    const startDate = translatePeriods(startPeriod, forceFirstDayOfWeek, 'onTabClick 1')
                    const endDate = translatePeriods(endPeriod, forceFirstDayOfWeek, 'onTabClick 2')

                    params['dates_list'] = [startDate, endDate, startPeriod, endPeriod]
                }
            } else {
                params['account_id'] = null
            }

            if ((currentTab === 'flextime' && val !== 'flextime') || (currentTab !== 'flextime' && val === 'flextime')) {
                const selectedTab = tabs.value.findIndex(tab => tab.value === val)
                const filters = getFilters(selectedTab, params['account_id'])
                filterController.initFilters(filters)
            }

            await urlAdapter.setUrlParametersFromObject(params)

            richDataTableController.setAllHeaders(fieldColumns.value)
            richDataTableController.setVisibleHeaders(visibleHeaders)
        };

        const getAssigneesByIds = (ids, users) => {
            if (!ids) return;
            return ids.map(id => {
                let user = users.find(x => x.id === id)
                if (user) {
                    return {
                        assigned_initials: user.initials,
                        assigned_avatar: user.avatar
                    };
                } else {
                    return {
                        assigned_initials: ""
                    };
                }
            });
        }

        const backendQuery = async (params) => {
            let resp

            try {
                resp = await ApiReport.getReport(params)
            } catch (e) {
                errorDialog.showError(e)
                richDataTableController.setRows([], 0)
                summary_childs.value = []
                return
            }

            report.value = Object.assign({}, report.value, resp);
            let time_entries = resp.time_entries;
            let childs = resp.childs;
            let tasks = resp.tasks;
            let users = resp.users;

            billableTime.value = resp.billable
            extraTime.value = resp.extra
            nonBillableTime.value = resp.non_billable
            estimationTime.value = resp.estimation
            balanceTime.value = resp.flextime_balance
            agreedTime.value = resp.flextime_expected
            totalTime.value = resp.total
            statistics.value = resp.statistics

            if (time_entries) {
                time_entries = time_entries.map(x => {
                    x.topic = topicStore.getTopicById(x.topic_id)
                    return x
                })

                time_entries = time_entries.map(x => {
                    x.assignees = getAssigneesByIds([x.person_id], users)
                    return x
                })

                richDataTableController.setRows(time_entries, resp.time_entries_count)
            } else if (childs) {
                childs = childs.map(x => {
                    if (x.client) {
                        x.client_id = x.client.id
                    }

                    return x
                })

                // TODO: getting summary view to work with our rich data table would take a lot of work
                // Maybe it should be even a separate component, but won't be using the rich data table for now
                summary_childs.value = childs
                richDataTableController.setRows(childs, resp.childs.length)
            } else if (tasks) {
                tasks = tasks.map(x => {
                    x.mappedAssignees = getAssigneesByIds(x.assignees, users)
                    x.client.default_person = {
                        assigned_initials: x.client.default_person?.initials,
                        assigned_avatar: x.client.default_person?.avatar
                    }
                    return x
                })

                richDataTableController.setRows(tasks, resp.task_count)
            }

            richDataTableController.setAllHeaders(fieldColumns.value)
        };

        richDataTableController.setLoadDataFunc(backendQuery)

        const onDownloadReport = async act => {
            // "create_report_excel", "create_report_pdf"
            const PROTOCOL = process.env.VUE_APP_PROTOCOL ? process.env.VUE_APP_PROTOCOL : "";
            const HOST = process.env.VUE_APP_HOST ? process.env.VUE_APP_HOST : "";
            const BASE_URL = `${PROTOCOL}://${HOST}`;
            const url = new URLSearchParams({
                ...filterController.getCurrentFilterValues(),
                ...richDataTableController.getOptions(),
                view: currentView.value,
                groupBy: urlAdapter.getUrlParameter('groupBy'),
                act,
                columns: urlAdapter.getUrlParameter('columns'),
                details: show_export_details.value,
                report_title: f_bookmarks("report").find(i => activeBookmarkRule(i))
                    ? f_bookmarks("report").find(i => activeBookmarkRule(i)).name
                    : $t("Report")
            }).toString();
            window.open(BASE_URL + "/api/report?" + url);
        };

        const onDownloadExcelConfirmed = (export_fields) => {
            // "create_report_excel", "create_report_pdf"
            const PROTOCOL = process.env.VUE_APP_PROTOCOL ? process.env.VUE_APP_PROTOCOL : "";
            const HOST = process.env.VUE_APP_HOST ? process.env.VUE_APP_HOST : "";
            const BASE_URL = `${PROTOCOL}://${HOST}`;
            const url = new URLSearchParams({
                ...filterController.getCurrentFilterValues(),
                ...richDataTableController.getOptions(),
                view: currentView.value,
                groupBy: urlAdapter.getUrlParameter('groupBy'),
                act: 'create_report_excel',
                columns: urlAdapter.getUrlParameter('columns'),
                details: show_export_details.value,

                // fixme:: export_fields should be csv list of requested fields (export_fields = client_fields,project_custom_fields)
                export_fields: export_fields ? 'client_fields' : '',

                report_title: f_bookmarks("report").find(i => activeBookmarkRule(i))
                    ? f_bookmarks("report").find(i => activeBookmarkRule(i)).name
                    : $t("Report")
            }).toString();
            window.open(BASE_URL + "/api/report?" + url);
        };

        const onDownloadExcel = () => {
            // todo :: support exporting summary client info and client fields (except relations)
            if (1 || currentView.value === 'summary' && groupBy.value.slice(0, 6) !== 'client') {
                onDownloadExcelConfirmed(false)
            } else {
                dialogModule.setConfirmOptions({
                    title: $t("Export report to Excel"),
                    text: $t("By default, we export the result of the selected filters with the columns of the configured table"),
                    options: [
                        {
                            name: 'exportAll',
                            text: $t("Add to export client custom fields"),
                            checked: false
                        }
                    ],
                    confirm_text: $t("Export"),
                    cancel_text: $t("Cancel"),

                    // fixme:: export_fields should be csv list of requested fields (export_fields = client_fields,project_custom_fields)
                    action: (selected) => onDownloadExcelConfirmed(selected.length),
                });
                dialogModule.openConfirmDialog();
            }
        }

        const onSaveFiltersClick = () => {
            setDialogOptions({
                component: () => import("@/views/Dialog/BookmarkDialog"),
                payload: {
                    type: "report",
                    title: $t("Save report")
                }
            });
            openDialog();
        };

        const showSnackbar = () => {
            data_changed_snackbar.value = true
        }
        const snackbarClose = function () {
            data_changed_snackbar.value = false;
        };
        const snackbarUpdate = function () {
            urlAdapter.updateUrlParameters()
            data_changed_snackbar.value = false;
        };

        const exportOptions = computed(() => {
            let options = [];
            let currentTab = urlAdapter.getUrlParameter('view')
            if (currentTab === 'flextime') return options;

            options.push({
                text: $t('Export PDF'),
                action: () => onDownloadReport('create_report_pdf')
            })
            options.push({
                text: $t('Export Excel'),
                action: () => onDownloadExcel()
            })
            return options;
        })

        const taskDeleteCallback = (fx) => {
            const rows = richDataTableController.getRows()

            if (urlAdapter.getUrlParameter('view') === 'time_entries') {
                const timeEntry = rows.find(x => fx(x));
                if (timeEntry) {
                    timeEntry.dirty = 1
                    showSnackbar()
                }
            } else {
                const task = rows.find(x => x.id === fx(x))
                if (task) {
                    task.dirty = 1
                    showSnackbar()
                }
            }
        }

        const taskChangeCallback = (changedTask) => {
            const rows = richDataTableController.getRows()

            if (urlAdapter.getUrlParameter('view') === 'time_entries') {
                const timeEntry = rows.find(x => x.task_id === changedTask.id);
                if (timeEntry) {
                    timeEntry.dirty = 1
                    showSnackbar()
                }
            } else {
                const task = rows.find(x => x.id === changedTask.id)
                if (task) {
                    task.dirty = 1
                    showSnackbar()
                }
            }
        }

        taskWsModule.addTaskDeleteCallback(taskDeleteCallback)
        taskWsModule.addTaskChangeCallback(taskChangeCallback)
        timerModule.addTaskChangeCallback(taskChangeCallback)

        filterController.startUrlWatcher()

        const showTimeFeedback = computed(() => {
            if (urlAdapter.getUrlParameter('sortBy') !== '') {
                return false
            }

            if (!urlAdapter.getUrlParameter('person_id_list')) {
                return false
            }

            if (urlAdapter.getUrlParameter('person_id_list').length !== 1) {
                return false
            }

            return true
        })

        const showNewDayFeedback = computed(() => {
            if (urlAdapter.getUrlParameter('sortBy') !== '') {
                return false
            }

            return true
        })

        const showTimeFeedbackOptions = computed(() => {
            if (!showTimeFeedback.value) {
                return false
            }

            if (urlAdapter.getUrlParameter('person_id_list')[0] !== current_account.value.id) {
                return false
            }

            return true
        })

        const friendly_selected_dates = computed(() => {
            let dates = urlAdapter.getUrlParameter('dates_list');
            if (dates) {
                let filter_dates;
                if (dates.length >= 3) {
                    const forceFirstDayOfWeek = urlAdapter.getUrlParameter('view') === 'flextime' ? 1 : null
                    filter_dates = [
                        translatePeriods(dates[2], forceFirstDayOfWeek, 'friendly_selected_dates 1'),
                        translatePeriods(dates[3], forceFirstDayOfWeek, 'friendly_selected_dates 2')
                    ]
                } else {
                    filter_dates = [formatDate(dates[0]), formatDate(dates[1])]
                }
                return "{0} - {1}".format(filter_dates[0], filter_dates[1]);
            }
            return "";
        });

        const detailedStatisticsClick = async () => {
            detailedStatisticsOpen.value = !detailedStatisticsOpen.value
            localStorage.setItem('isReportStatisticsOpen', detailedStatisticsOpen.value)

            await Vue.nextTick()
            window.dispatchEvent(new Event('resize'));
        }

        const graphData = computed(() => {
            if (statistics.value) {
                return statistics.value.graph_data
            }
            return null
        })

        const totalTasks = computed(() => {
            if (statistics.value) {
                return statistics.value.tasks_total
            }
        })

        const billingInfo = computed(() => {
            return {
                billable: billableTime.value,
                nonBillable: nonBillableTime.value,
                total: totalTime.value
            }
        })

        const actionItems = computed(() => {
            if (periodChart.value && periodChart.value.$refs && periodChart.value.$refs.reportChart && periodChart.value.$refs.reportChart.chart) {
                const chart = periodChart.value.$refs.reportChart.chart

                return [
                    {
                        text: $t('Download SVG'),
                        action: () => chart.exports.exportToSVG()
                    },
                    {
                        text: $t('Download PNG'),
                        action: () => chart.exports.exportToPng()
                    },
                    {
                        text: $t('Download CSV'),
                        action: () => chart.exports.exportToCSV({
                            series: chart.series.w.config.series,
                            columnDelimiter: ','
                        })
                    },
                ]
            } else {
                return []
            }
        })

        return {
            group_toggle_reset_unique,
            show_export_details,
            tabs,
            input_data,
            fieldColumns,
            data_changed_snackbar,
            totalColumns,
            f_bookmarks,
            navigate_bookmark,
            activeBookmarkRule,
            onDeleteBookmarkClick,
            backendQuery,
            onSaveFiltersClick,
            onDownloadReport,
            selectedTabIndex,
            onTabClick,
            snackbarClose,
            snackbarUpdate,
            filterController,
            urlAdapter,
            richDataTableController,
            exportOptions,
            summary_childs,
            showTimeFeedback,
            tableWidth,
            time_custom_fields,
            showSnackbar,
            client_groups,
            friendly_selected_dates,
            isFlextimeActive,
            filters,
            companiesWithFlextime,
            showTimeFeedbackOptions,
            showNewDayFeedback,
            formatTimeDuration,
            detailedStatisticsOpen,
            detailedStatisticsClick,
            graphData,
            billingInfo,
            totalTasks,
            periodChart,
            actionItems
        };
    }
}
;
</script>
